import { Card, Stack, TextField, Typography } from "@mui/material";
import RouterHelper from "../../utilities/router-helper";
import { useUnitListStore } from "./useUnitListStore";
import { Box } from "@mui/system";
import { StatusTabs } from "./StatusTabs";
import { UnitsTable } from "./UnitsTable";
import TitleHeader from "../../shared/title-header";
import useWindowSize from "../../utilities/useWindowSize";
import RelationshipSelector from "../../shared/RelationshipSelector";
import LocationSelector from "../../shared/LocationSelector";
import { useEffect } from "react";
import { useAuth } from "../../auth";
import { mixpanelEventHandler } from "../../utilities/reactMixpanelHandler";

type UnitListProps = {
  routerHelper: RouterHelper;
};

function UnitList({ routerHelper }: UnitListProps) {
  const auth = useAuth();
  const { selectedRelationshipId, selectedLocationId, searchTerm, setSearchTerm, setSelectedRelationshipId, setSelectedLocationId } =
    useUnitListStore((state) => ({
      selectedRelationshipId: state.selectedRelationshipId,
      selectedLocationId: state.selectedLocationId,
      searchTerm: state.searchTerm,
      setSearchTerm: state.setSearchTerm,
      setSelectedRelationshipId: state.setSelectedRelationshipId,
      setSelectedLocationId: state.setSelectedLocationId,
    }));
  const size = useWindowSize();

  const pendingSelection = !selectedLocationId || !selectedRelationshipId;

  // Set default selectedRelationship when data loads and none is selected
  useEffect(() => {
    if (!selectedRelationshipId) {
      const relationshipId = routerHelper.getRelationshipId() || auth.user.relationshipId;
      setSelectedRelationshipId(relationshipId);
    }
  }, [selectedRelationshipId, setSelectedRelationshipId]);

  // Track page view
  useEffect(() => {
    mixpanelEventHandler("Units - Page View", {
      userEmail: auth.user?.email,
      userId: auth.user?.id,
      relationshipId: selectedRelationshipId,
    });
  }, [auth.user?.email, auth.user?.id, selectedRelationshipId]);

  // Add tracking separately via useEffect
  useEffect(() => {
    if (selectedLocationId) {
      mixpanelEventHandler("Units - Location Selected", {
        userEmail: auth.user?.email,
        userId: auth.user?.id,
        relationshipId: selectedRelationshipId,
        locationId: selectedLocationId.toString(),
      });
    }
  }, [selectedLocationId]); // Track when location changes

  return (
    <Box>
      <TitleHeader title="Units"></TitleHeader>
      <Stack sx={{ height: size.height - 186 }} spacing={2} overflow="auto">
        <Stack direction="row" spacing={3}>
          <RelationshipSelector
            nonRouteSelectedRelationshipIdValue={selectedRelationshipId}
            routerHelper={routerHelper}
            nonRoute
            nonRouteCallback={(relationshipId: string) => {
              setSelectedRelationshipId(relationshipId);
              setSelectedLocationId(null);
            }}
          />
          <LocationSelector
            excludeAllLocationsOption
            locationId={selectedLocationId}
            setLocationId={setSelectedLocationId}
            relationshipId={selectedRelationshipId}
          />
        </Stack>
        {pendingSelection ? (
          <Typography>Select a location to view units</Typography>
        ) : (
          <Stack flex={1} overflow="auto" component={Card} spacing={2}>
            <Box sx={{ padding: 1.5 }}>
              <StatusTabs />
            </Box>
            <Box sx={{ paddingX: 2 }}>
              <TextField
                fullWidth
                label="Search tenant name or unit"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  mixpanelEventHandler("Units - Search", {
                    userEmail: auth.user?.email,
                    userId: auth.user?.id,
                    relationshipId: selectedRelationshipId,
                    locationId: selectedLocationId?.toString(),
                    searchTerm: e.target.value,
                  });
                }}
              />
            </Box>
            <UnitsTable />
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export { UnitList };
